const getI18n = () => ( window.var_from_php && window.var_from_php.i18n ) || {};

const getPhoneDigits = value => String( value || '' ).replace( /\D/g, '' );

function getMainFormFields( form ) {
	return {
		nameInput: form.querySelector( '[name="name"]' ),
		phoneInput: form.querySelector( '[name="phone"]' ),
		phoneCountryCodeInput: form.querySelector( '[name="phone_country_code"]' ),
		emailInput: form.querySelector( '[name="email"]' ),
		consentInput: form.querySelector( '[name="consent"]' ),
	};
}

function getFullPhone( form ) {
	const { phoneInput, phoneCountryCodeInput } = getMainFormFields( form );
	const phone = phoneInput ? phoneInput.value.trim() : '';
	const countryCode = phoneCountryCodeInput ? phoneCountryCodeInput.value.trim() : '';

	if ( !phone ) {
		return '';
	}

	if ( phone.charAt( 0 ) === '+' ) {
		return phone;
	}

	return `${ countryCode }${ phone.replace( /^0+/, '' ) }`;
}

function updateMainFormSubmitState( form ) {
	const { consentInput } = getMainFormFields( form );
	const submitButton = form.querySelector( '[type="submit"]' );

	if ( !submitButton ) {
		return;
	}

	submitButton.disabled = !consentInput || !consentInput.checked;
}

export function clearMainFormValidation( form ) {
	if ( !form ) {
		return;
	}

	form.querySelectorAll( '.block-main__field' ).forEach( field => {
		field.classList.remove( '!border-b-[#E53935]' );
	} );
}

export function validateMainForm( form ) {
	const i18n = getI18n();
	const { nameInput, phoneInput, emailInput } = getMainFormFields( form );
	const invalidFields = [];

	const name = nameInput ? nameInput.value.trim() : '';
	const phone = getFullPhone( form );
	const email = emailInput ? emailInput.value.trim() : '';
	const phoneDigits = getPhoneDigits( phone );

	if ( name.length < 2 ) {
		invalidFields.push( 'name' );
	}

	if ( !phone ) {
		invalidFields.push( 'phone' );
	} else if ( phoneDigits.length < 9 ) {
		invalidFields.push( 'phone' );
	}

	if ( !emailInput || !email || !emailInput.validity.valid ) {
		invalidFields.push( 'email' );
	}

	clearMainFormValidation( form );

	invalidFields.forEach( fieldName => {
		const input = form.querySelector( `[name="${fieldName}"]` );
		const wrapper = input ? input.closest( '.block-main__field' ) : null;

		if ( wrapper ) {
			wrapper.classList.add( '!border-b-[#E53935]' );
		}
	} );

	if ( invalidFields.length ) {
		const firstInvalidInput = form.querySelector( `[name="${invalidFields[0]}"]` );

		if ( firstInvalidInput && typeof firstInvalidInput.focus === 'function' ) {
			firstInvalidInput.focus();
		}
	}

	return {
		isValid: invalidFields.length === 0,
		invalidFields,
		i18n,
	};
}

export function openMainFormPopup() {
	const popup = document.getElementById( 'main_form_popup' );
	const body = document.querySelector( 'body' );

	if ( !popup ) {
		return;
	}

	document.querySelectorAll( '.js-popup' ).forEach( item => {
		item.classList.remove( 'show' );

		if ( item.id === 'main_form_popup' || item.id === 'main_form_success_popup' ) {
			item.setAttribute( 'aria-hidden', 'true' );
		}
	} );

	const mobileMenu = document.querySelector( '.js-site-menu' );

	if ( mobileMenu && !mobileMenu.classList.contains( 'hidden' ) ) {
		mobileMenu.classList.add( 'hidden' );
		mobileMenu.setAttribute( 'aria-hidden', 'true' );
	}

	popup.classList.add( 'show' );
	popup.setAttribute( 'aria-hidden', 'false' );
	body && body.classList.add( 'menu-open' );

	const nameInput = popup.querySelector( 'input[name="name"]' );

	if ( nameInput && typeof nameInput.focus === 'function' ) {
		window.setTimeout( () => nameInput.focus(), 100 );
	}
}

export function openMainFormSuccessPopup() {
	const popup = document.getElementById( 'main_form_success_popup' );
	const body = document.querySelector( 'body' );

	if ( !popup ) {
		return;
	}

	document.querySelectorAll( '.js-popup' ).forEach( item => {
		item.classList.remove( 'show' );

		if ( item.id === 'main_form_popup' || item.id === 'main_form_success_popup' ) {
			item.setAttribute( 'aria-hidden', 'true' );
		}
	} );

	popup.classList.add( 'show' );
	popup.setAttribute( 'aria-hidden', 'false' );
	body && body.classList.add( 'menu-open' );
}

export async function handleMainFormSubmit( form ) {
	const { consentInput } = getMainFormFields( form );

	if ( !consentInput || !consentInput.checked ) {
		updateMainFormSubmitState( form );
		return false;
	}

	const validation = validateMainForm( form );

	if ( !validation.isValid ) {
		return false;
	}

	const submitButton = form.querySelector( '[type="submit"]' );

	if ( submitButton ) {
		submitButton.disabled = true;
	}

	try {
		const formData = new FormData( form );

		formData.set( 'phone', getFullPhone( form ) );
		formData.append( 'action', 'submit_main_form' );
		formData.append( 'nonce', window.var_from_php?.main_form_nonce || '' );

		const response = await fetch( window.var_from_php.ajax_url, {
			method: 'POST',
			body: formData,
		} );

		const result = await response.json();

		if ( result.success ) {
			form.reset();
			clearMainFormValidation( form );
			updateMainFormSubmitState( form );
			openMainFormSuccessPopup();
			return true;
		}

		if ( result.data && Array.isArray( result.data.fields ) ) {
			clearMainFormValidation( form );

			result.data.fields.forEach( fieldName => {
				if ( fieldName === 'consent' ) {
					return;
				}

				const input = form.querySelector( `[name="${fieldName}"]` );
				const wrapper = input ? input.closest( '.block-main__field' ) : null;

				if ( wrapper ) {
					wrapper.classList.add( '!border-b-[#E53935]' );
				}
			} );
		}

		return false;
	} catch ( error ) {
		return false;
	} finally {
		updateMainFormSubmitState( form );
	}
}

export function initMainForms() {
	document.querySelectorAll( '[data-role="main-form"]' ).forEach( form => {
		if ( form.dataset.mainFormReady === 'true' ) {
			return;
		}

		form.dataset.mainFormReady = 'true';
		updateMainFormSubmitState( form );

		const consentInput = form.querySelector( '[name="consent"]' );

		if ( consentInput ) {
			consentInput.addEventListener( 'change', () => {
				updateMainFormSubmitState( form );
			} );
		}

		form.addEventListener( 'submit', async event => {
			event.preventDefault();
			await handleMainFormSubmit( form );
		} );
	} );
}

export function initMainFormValidationClear() {
	document.body.addEventListener( 'input', event => {
		const field = event.target.closest( '.block-main__form [name]' );

		if ( !field || field.name === 'consent' ) {
			return;
		}

		const wrapper = field.closest( '.block-main__field' );

		if ( wrapper && field.value.trim() !== '' ) {
			wrapper.classList.remove( '!border-b-[#E53935]' );
		}
	} );
}
